import type {
    ParticipantAccountStatus,
    YaasPosition,
    YaasPositionCardData,
    YaasWalletResponse,
    YaasWalletStats
} from "@solomon-apps/shared";
import { formatAdvertisedApy, useWallet } from "@solomon-apps/shared";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { getEarliestPositionDate, transformPosition, transformWalletStats } from "./helpers";
import { platformStatsQuery } from "./platformStatsQuery";
import { useRetailAccount } from "./useRetailAccount";
import { useWalletView } from "./useWalletView";

/**
 * Result type for useYaasWalletData hook
 */
export interface UseYaasWalletDataResult {
    /** Transformed wallet statistics */
    walletStats: YaasWalletStats | null;
    /** Transformed position cards for display */
    positions: YaasPositionCardData[];
    /** User's YaaS account status */
    yaasStatus: ParticipantAccountStatus | null;
    /** Account returned by the wallet endpoint, even when it has no positions. */
    account: YaasWalletResponse["account"] | null;
    /** Whether user is whitelisted for YaaS */
    isYaasWhitelisted: boolean;
    /** Loading state */
    isLoading: boolean;
    /** Error object if request failed */
    error: Error | null;
    /** Whether wallet is connected */
    isWalletConnected: boolean;
    /** Refetch function */
    refetch: () => void;
    /** ISO date of earliest position (for pending earnings calculation) */
    earliestPositionCreatedAt: string | null;
    /** Account-level payout mode; null when no account or the account
     *  predates the mode. */
    distributionMode: "stream" | "claim" | null;
    /** Retail-program effective min-balance threshold (USDv); null when
     *  the backend has no retail policy configured. */
    minBalance: string | null;
}

/**
 * Hook to fetch YaaS wallet data and positions
 *
 * Fetches:
 * - User's YaaS account info and positions
 * - Global YaaS TVL statistics
 *
 * Features:
 * - Auto-refetches every 2 minutes
 * - Handles not-whitelisted users gracefully
 * - Transforms raw API data to display-ready format
 *
 * @param averageAPY - Current protocol APY (for position APY display)
 * @returns YaaS wallet data result
 *
 * @example
 * ```tsx
 * const { walletStats, positions, isYaasWhitelisted, isLoading } = useYaasWalletData(14.5);
 *
 * if (!isYaasWhitelisted) {
 *   return <AccessDenied />;
 * }
 *
 * return <PositionsList positions={positions} />;
 * ```
 */
export function useYaasWalletData(averageAPY: number | null = null): UseYaasWalletDataResult {
    const { connected } = useWallet();
    const selectedAccount = useRetailAccount();
    const address = selectedAccount.address;

    // Fetch wallet-specific data
    const walletQuery = useWalletView(address, connected && !!address && selectedAccount.ready);

    // Fetch global YaaS stats (for TVL)
    const statsQuery = useQuery(platformStatsQuery);

    // Extract status from account object (null if no YaaS account exists)
    const yaasStatus = walletQuery.data?.account?.status ?? null;

    // Check if whitelisted - has Active status (null data means no account)
    const isYaasWhitelisted = yaasStatus === "Active";

    // Transform wallet stats
    const walletStats = useMemo((): YaasWalletStats | null => {
        if (!walletQuery.data?.account) return null;
        return transformWalletStats(walletQuery.data, statsQuery.data);
    }, [walletQuery.data, statsQuery.data]);

    // Calculate APY values
    const apy = averageAPY !== null ? averageAPY : 0;
    const formattedApy = formatAdvertisedApy(averageAPY);

    // Transform positions for display
    const positions = useMemo((): YaasPositionCardData[] => {
        if (!walletQuery.data?.account) return [];

        const account = walletQuery.data.account;
        const accountFallback = {
            rewardsDestinationAddress: account.rewardsDestinationAddress,
            address: account.address
        };
        return walletQuery.data.programPositions.map((pos: YaasPosition) => {
            return transformPosition(pos, apy, formattedApy, accountFallback);
        });
    }, [walletQuery.data, apy, formattedApy]);

    // Get earliest position date
    const earliestPositionCreatedAt = useMemo((): string | null => {
        return getEarliestPositionDate(walletQuery.data?.programPositions ?? []);
    }, [walletQuery.data?.programPositions]);

    return {
        walletStats,
        positions,
        yaasStatus,
        account: walletQuery.data?.account ?? null,
        isYaasWhitelisted,
        isLoading: walletQuery.isLoading || statsQuery.isLoading,
        // A failed refresh keeps the last usable account, holdings and policy.
        error: (walletQuery.data ? null : walletQuery.error) ?? (statsQuery.data ? null : statsQuery.error),
        isWalletConnected: connected,
        refetch: (): void => {
            void walletQuery.refetch();
            void statsQuery.refetch();
        },
        earliestPositionCreatedAt,
        distributionMode: walletQuery.data?.account?.distributionMode ?? null,
        minBalance: statsQuery.data?.minBalance ?? null
    };
}
